// The booking's price breakdown, as one pure function. This is getPrice() from
// Bookings.jsx (and the copy of it inside deleteCustomItem) moved out so it can be tested,
// plus the Review step's new inputs: discount as a % or a flat amount, a quantity per service
// line and a tip. Each service line can have its own discount (saved per offering as
// line_discount); the subtotal is the lines after their discounts, and the order Discount is
// a separate, overall discount on top of that subtotal.
//
// What the API reads from priceSection (serviceProviders.service.js): any row whose name
// contains "tax" / "discount" / "total" is a summary row, every other non-custom, non-part
// row is a service line. So: no extra rows may be added (a "Tip" row would be read as a
// service), and the tip and every discount are folded into the Discount / Total rows.

export const DISCOUNT_FLAT = "flat";
export const DISCOUNT_PCT = "pct";

export const toNum = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

// A discount spec { type: "flat" | "pct", value } applied to `base`. A % is capped at 100,
// and nothing is ever negative.
export function discountValue(spec, base) {
  const v = toNum(spec?.value);
  if (v <= 0 || toNum(base) <= 0) return 0;
  if (spec?.type === DISCOUNT_PCT) return round2((toNum(base) * Math.min(v, 100)) / 100);
  return round2(v);
}

// A vendor coupon (percent off) from Branding & Offers > Coupons. partner_ids / service_ids are
// where it is limited to; an empty list means every profile / every service.
// Today as YYYY-MM-DD in the vendor's own (browser) time zone.
const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const couponAppliesToBooking = (coupon, partnerId, serviceIds = []) => {
  if (!coupon) return false;
  // Only within its dates: not before the start date, not after the end date (both days included).
  const today = localToday();
  if (coupon.start_date && today < coupon.start_date) return false;
  if (coupon.end_date && today > coupon.end_date) return false;
  const partners = (coupon.partner_ids || []).map(String);
  const services = (coupon.service_ids || []).map(String);
  if (partners.length && !partners.includes(String(partnerId))) return false;
  if (services.length && !serviceIds.some((id) => services.includes(String(id)))) return false;
  return true;
};

export const lineKey = (item) => String(item?.service_offering_id);

// What the per-line discounts add up to (each capped at its own line).
export function lineDiscountsTotal({ serviceData = [], lineDiscounts = {}, lineQty = {} }) {
  let total = 0;
  for (const eachData of serviceData) {
    if (!eachData?.added) continue;
    const amount = toNum(eachData.amount) * quantityOf(lineQty, eachData);
    total += Math.min(discountValue(lineDiscounts?.[lineKey(eachData)], amount), amount);
  }
  return round2(total);
}

export const quantityOf = (lineQty, item) => {
  const q = Math.floor(toNum(lineQty?.[lineKey(item)]));
  return q >= 1 ? q : 1;
};

export function serviceLineName(eachData) {
  let name = eachData.service_name + "- ";
  if (eachData.service_type_name && eachData.service_type_name !== "null") {
    name += eachData.service_type_name + "- ";
  }
  return name + eachData.service_offering_name;
}

/**
 * @param serviceData        every offering of the booking (added or not) - only `added` ones are charged
 * @param basis              the current priceSection: its custom (isCustom) and part (isPart) rows are kept
 * @param editPartsSubtotal  live parts subtotal of a saved booking (those parts live in ezp_booking_parts)
 * @param taxPercentage      vendor tax %, charged on the subtotal after every discount (line
 *                           discounts, the order discount and the coupon)
 * @param discountSpec       the overall order discount { type, value }, applied to the subtotal
 * @param lineDiscounts      { [service_offering_id]: { type, value } } - each line's own discount
 * @param lineQty            { [service_offering_id]: quantity } (new bookings only)
 * @param tipPercent         % of the subtotal, added to the Total only
 * @param coupon             the chosen vendor coupon { discount_percent, partner_ids, service_ids } or null; its
 *                           amount is folded into the Discount row (the API only reads Discount / Total)
 * @param partnerId          the booking's profile, to check the coupon against
 */
export function computePriceSection({
  serviceData = [],
  basis = [],
  editPartsSubtotal = 0,
  taxPercentage = 0,
  discountSpec = { type: DISCOUNT_FLAT, value: 0 },
  lineDiscounts = {},
  lineQty = {},
  tipPercent = 0,
  coupon = null,
  partnerId = null
}) {
  const prices = [];
  const addedLines = [];
  let servicesTotal = 0;
  let lineDiscountTotal = 0;

  for (const eachData of serviceData) {
    if (!eachData?.added) continue;
    const qty = quantityOf(lineQty, eachData);
    const gross = toNum(eachData.amount) * qty;
    const lineDiscount = Math.min(discountValue(lineDiscounts?.[lineKey(eachData)], gross), gross);
    const amount = gross - lineDiscount;

    prices.push({
      name: serviceLineName(eachData),
      service_id: eachData.service_id,
      service_name: eachData.service_name,
      service_type_id: eachData.service_type_id,
      service_type_name: eachData.service_type_name,
      service_offering_id: eachData.service_offering_id,
      service_offering_name: eachData.service_offering_name,
      // Untouched when quantity is 1, so a row keeps the exact value the user typed.
      amount: qty === 1 ? eachData.amount : gross,
      // Saved on the offering's row; `amount` stays the price before it.
      line_discount: lineDiscount
    });

    servicesTotal += amount;
    lineDiscountTotal += lineDiscount;
    addedLines.push({ service_id: eachData.service_id, amount });
  }

  // Preserve custom items / staged parts already in the price section across rebuilds.
  const customRows = (basis || []).filter((item) => item?.isCustom);
  const partRows = (basis || []).filter((item) => item?.isPart);
  const customTotal = customRows.reduce((sum, item) => sum + toNum(item.amount), 0);
  const partsTotal = partRows.reduce((sum, item) => sum + toNum(item.amount), 0);
  const editParts = toNum(editPartsSubtotal);

  const subtotal = servicesTotal + customTotal + partsTotal + editParts;
  // A coupon on specific services discounts only those lines; one on all services, the whole subtotal.
  let couponAmount = 0;
  if (couponAppliesToBooking(coupon, partnerId, addedLines.map((l) => l.service_id))) {
    const limitTo = (coupon.service_ids || []).map(String);
    const base = limitTo.length
      ? addedLines.filter((l) => limitTo.includes(String(l.service_id))).reduce((sum, l) => sum + l.amount, 0)
      : subtotal;
    couponAmount = round2((base * Math.min(Math.max(toNum(coupon.discount_percent), 0), 100)) / 100);
  }
  const discountAmount = discountValue(discountSpec, subtotal) + couponAmount;
  // Tax is charged on what is left after every discount (item, overall and coupon).
  const taxAmount = (Math.max(subtotal - discountAmount, 0) * toNum(taxPercentage)) / 100;
  const tipAmount = (subtotal * toNum(tipPercent)) / 100;
  const total = subtotal + taxAmount - discountAmount + tipAmount;

  const hasContent = prices.length > 0 || customTotal > 0 || partsTotal > 0 || editParts > 0;
  const rows = hasContent
    ? [
        ...prices,
        { name: "Discount", amount: discountAmount },
        ...customRows,
        ...partRows,
        { name: `Tax (${taxPercentage}%)`, amount: taxAmount.toFixed(2) },
        { name: "Total", amount: total.toFixed(2) }
      ]
    : [];

  return {
    rows,
    subtotal,
    taxAmount,
    discountAmount,
    lineDiscountTotal: round2(lineDiscountTotal),
    couponAmount,
    tipAmount,
    total,
    hasContent
  };
}

export const totalOf = (priceSection) =>
  toNum((priceSection || []).find((item) => item.name === "Total")?.amount);

// Messages for the Review step; an empty list means the money adds up.
export function pricingProblems({ result, paidAmount }) {
  const problems = [];
  if (result.hasContent && result.total < 0) {
    problems.push("The discount is more than the order total.");
  }
  if (toNum(paidAmount) < 0) problems.push("Paid amount cannot be negative.");
  return problems;
}
